import Banner from "@components/Banner"
import Helmet from "@components/Helmet"
import Layout from "@components/Layout/hoc/withDatoData"
import VerticalSpace from "@components/VerticalSpace"
import useGtmEvents from "@hooks/useGtmEvents"
import useScrollHandler from "@hooks/useScrollHandler"
import {
  getProSeriesCategoryUrl,
  getProSeriesProductStyleUrl,
  getProSeriesSubcategoryUrl,
  getProSeriesUrl,
} from "@utils/urls"
import { SortMethods } from "@views/common"
import ConfigBanner from "@views/common/Banner/Banner"
import DesktopBreadcrumbs from "@views/common/Breadcrumbs/DesktopBreadcrumbs"
import MobileBreadcrumbs from "@views/common/Breadcrumbs/MobileBreadcrumbs"
import Categories from "@views/common/Categories"
import Empty from "@views/common/Empty"
import FilterButton from "@views/common/FilterButton"
import Filters from "@views/common/Filters"
import Products from "@views/common/Products"
import ProductStyles from "@views/common/ProductStyles"
import SortDropdown from "@views/common/SortDropdown"
import _ from "lodash"
import { useRouter } from "next/router"
import React from "react"

const productListRef = React.createRef<HTMLDivElement>()

const ProSeries: any = ({
  title,
  category,
  subcategory,
  style,
  data,
  dataLoaded,
  module = "automotive",
  series = "Pro-Series",
}) => {
  const router = useRouter()

  const allProducts: Array<any> = React.useMemo(
    () => data?.allProducts || [],
    [title, category, subcategory, style, data, dataLoaded]
  )

  const proSeriesCategories = React.useMemo(() => {
    return _.uniqBy(
      allProducts
        .map((product) => product.proSeriesCategory)
        .filter((category) => !!category)
        .sort(),
      ({ slug }) => slug
    )
  }, [allProducts])
  const proSeriesSubcategories = React.useMemo(() => {
    return _.uniqBy(
      allProducts
        .map((product) => product.proSeriesSubcategory)
        .filter((subcategory) => !!subcategory)
        .sort(),
      ({ slug }) => slug
    )
  }, [allProducts])

  const productSizes = React.useMemo(() => {
    return _.uniq(
      allProducts
        .map((product) => product.productSize?.size)
        .filter((size) => !!size)
        .sort()
    )
  }, [allProducts])
  const productStyles = React.useMemo(() => {
    return _.sortBy(
      _.uniqBy(
        allProducts
          .map((product) => product.productStyle)
          .filter((style) => !!style)
          .sort(),
        ({ slug }) => slug
      ),
      ({ name }) => name
    )
  }, [allProducts])

  const [products, setProducts] = React.useState<any>({
    [SortMethods.ByFeatured]: allProducts,
    [SortMethods.ByPriceAsc]: _.sortBy(allProducts, ({ price }) => price),
    [SortMethods.ByPriceDesc]: _.reverse(
      _.sortBy(allProducts, ({ price }) => price)
    ),
    [SortMethods.ByNameAsc]: _.sortBy(
      allProducts,
      ({ productName }) => productName
    ),
    [SortMethods.ByNameDesc]: _.reverse(
      _.sortBy(allProducts, ({ productName }) => productName)
    ),
  })

  React.useEffect(() => {
    setProducts({
      [SortMethods.ByFeatured]: allProducts,
      [SortMethods.ByPriceAsc]: _.sortBy(allProducts, ({ price }) =>
        parseFloat(price)
      ),
      [SortMethods.ByPriceDesc]: _.reverse(
        _.sortBy(allProducts, ({ price }) => parseFloat(price))
      ),
      [SortMethods.ByNameAsc]: _.sortBy(
        allProducts,
        ({ productName }) => productName
      ),
      [SortMethods.ByNameDesc]: _.reverse(
        _.sortBy(allProducts, ({ productName }) => productName)
      ),
    })
  }, [allProducts])

  const [sortMethod, setSortMethod] = React.useState<SortMethods>(
    SortMethods.ByFeatured
  )

  const onSort = (sortMethod) => {
    //Push state here
    const currentParams = router.query

    const currentPath = style
      ? getProSeriesProductStyleUrl(
          category.slug,
          subcategory.slug,
          style.slug,
          series.toLowerCase()
        )
      : subcategory
      ? getProSeriesSubcategoryUrl(
          category.slug,
          subcategory.slug,
          series.toLowerCase()
        )
      : category
      ? getProSeriesCategoryUrl(category.slug, series.toLowerCase())
      : getProSeriesUrl(series.toLowerCase())

    delete currentParams.category
    delete currentParams.subcategory
    delete currentParams.style

    currentParams["sort"] = sortMethod

    router.push({ pathname: currentPath, query: currentParams }, undefined, {
      shallow: true,
    })

    setSortMethod(sortMethod)
  }

  const [renderedProductCount, setRenderedProductCount] = React.useState(18)

  const onScrollHandler = () => {
    if (
      typeof window === "undefined" ||
      !productListRef ||
      !productListRef.current ||
      allProducts.length <= renderedProductCount
    ) {
      return
    }

    const productListNodes = productListRef.current.childNodes
    const positionReference: any =
      productListNodes[productListNodes.length - 9] ||
      productListNodes[productListNodes.length - 6] ||
      productListNodes[productListNodes.length - 3]

    if (positionReference?.getBoundingClientRect().top < window.innerHeight) {
      setRenderedProductCount(renderedProductCount + 18)
    }
  }

  useScrollHandler({
    onScroll: onScrollHandler,
    runForProps: [data, dataLoaded, renderedProductCount],
  })

  const [renderedProducts, setRenderedProducts] = React.useState(
    _.take(products[SortMethods.ByFeatured], renderedProductCount)
  )

  const filterPropMap = {
    Category: {
      prop: "proSeriesCategory",
      key: "name",
    },
    Type: {
      prop: "proSeriesSubcategory",
      key: "name",
    },
    Style: {
      prop: "productStyle",
      key: "name",
    },
    Sizes: {
      prop: "productSize",
      key: "size",
    },
  }

  const filters = {
    Category: proSeriesCategories || null,
    Type: proSeriesSubcategories || null,
    Style: productStyles || null,
    Sizes: productSizes || null,
  } as any

  const [activeFilters, setActiveFilters] = React.useState<{
    [filterName: string]: Array<string>
  }>({
    Category: ([] as string[]).concat(
      router.query.Category ? (router.query.Category as string[]) : []
    ),
    Type: ([] as string[]).concat(
      router.query.Type ? (router.query.Type as string[]) : []
    ),
    Style: ([] as string[]).concat(
      router.query.Style ? (router.query.Style as string[]) : []
    ),
    Sizes: ([] as string[]).concat(
      router.query.Sizes ? (router.query.Sizes as string[]) : []
    ),
  })

  const onFilter = (filters) => {
    const currentPath = style
      ? getProSeriesProductStyleUrl(
          category.slug,
          subcategory.slug,
          style.slug,
          series
        )
      : subcategory
      ? getProSeriesSubcategoryUrl(category.slug, subcategory.slug, series)
      : category
      ? getProSeriesCategoryUrl(category.slug, series)
      : getProSeriesUrl(
          module == "automotive" ? "pro-series" : series.toLowerCase()
        )

    //Add push state here
    let params = {}
    for (var prop in filters) {
      if (Object.prototype.hasOwnProperty.call(filters, prop)) {
        params[prop] = filters[prop]
      }
    }

    //Add sort param if present
    const currentParams = router.query

    if (currentParams.sort) {
      params["sort"] = currentParams.sort
    }

    router.push({ pathname: currentPath, query: params }, undefined, {
      shallow: true,
    })

    setActiveFilters(filters)
  }

  const onClearFilter = () => {
    setActiveFilters({
      Category: [],
      Type: [],
      Style: [],
      Sizes: [],
    })
  }

  React.useEffect(() => {
    // console.log(router.query)
    setActiveFilters({
      Category: ([] as string[]).concat(
        router.query.Category ? (router.query.Category as string[]) : []
      ),
      Type: ([] as string[]).concat(
        router.query.Type ? (router.query.Type as string[]) : []
      ),
      Style: ([] as string[]).concat(
        router.query.Style ? (router.query.Style as string[]) : []
      ),
      Sizes: ([] as string[]).concat(
        router.query.Sizes ? (router.query.Sizes as string[]) : []
      ),
    })

    if (router.query.sort) {
      setSortMethod(router.query.sort as SortMethods)
    }
  }, [router.query])

  React.useEffect(() => {
    onClearFilter()
  }, [title, category, subcategory, style, data, dataLoaded])

  React.useEffect(() => {
    const filtersToApply = Object.keys(activeFilters).filter(
      (key) => activeFilters[key].length > 0
    )
    const sortedProducts = products[sortMethod]
    const filteredProducts =
      filtersToApply.length > 0
        ? sortedProducts.filter((product) => {
            let filtered = true
            filtersToApply.forEach((key) => {
              const useMap = !!filterPropMap[key].key

              if (
                useMap &&
                product[filterPropMap[key].prop] &&
                activeFilters[key].indexOf(
                  product[filterPropMap[key].prop][filterPropMap[key].key]
                ) === -1
              ) {
                filtered = false
              } else if (
                !useMap &&
                activeFilters[key].indexOf(product[filterPropMap[key].prop]) ===
                  -1
              ) {
                filtered = false
              }
            })
            return filtered
          })
        : sortedProducts
    setRenderedProducts(_.take(filteredProducts, renderedProductCount))
  }, [allProducts, products, sortMethod, activeFilters, renderedProductCount])

  const [showMobileFilters, setShowMobileFilters] =
    React.useState<boolean>(false)

  const openMobileFilters = (e) => {
    e.stopPropagation()
    e.preventDefault()
    setShowMobileFilters(true)
  }

  const closeMobileFilters = () => {
    setShowMobileFilters(false)
  }

  let styles,
    categories,
    subcategories,
    header,
    subheader,
    body,
    breadcrumbs,
    image

  if (style) {
    header = style.name
    subheader = `Pro-Series ${category.name} ${subcategory.name}`
    body = style.bannerBody
    image = category.bannerImage?.responsiveImage
    breadcrumbs = [
      {
        link: getProSeriesUrl(),
        label: "Pro-Series",
      },
      {
        link: getProSeriesCategoryUrl(category.slug),
        label: category.name,
      },
      {
        link: getProSeriesSubcategoryUrl(category.slug, subcategory.slug),
        label: subcategory.name,
      },
      {
        link: "#",
        label: style.name,
      },
    ]
  } else if (subcategory) {
    styles = data?.collectionsConfiguration?.proSeriesProductStyles
      ?.filter(({ slug }) => {
        const thisStyle = productStyles.find(
          (productStyle) => productStyle.slug === slug
        )
        return !!thisStyle
      })
      .map((productStyle) => ({
        url: getProSeriesProductStyleUrl(
          category.slug,
          subcategory.slug,
          productStyle.slug
        ),
        image: productStyle.icon?.responsiveImage,
        title: productStyle.name,
      }))

    header = subcategory.name
    subheader = `Pro-Series ${category.name}`
    body = subcategory.bannerBody
    image = category.bannerImage?.responsiveImage
    breadcrumbs = [
      {
        link: getProSeriesUrl(),
        label: "Pro-Series",
      },
      {
        link: getProSeriesCategoryUrl(category.slug),
        label: category.name,
      },
      {
        link: "#",
        label: subcategory.name,
      },
    ]
  } else if (category) {
    const thisCategory = data?.collectionsConfiguration?.proSeries?.find(
      ({ proSeriesCategory }) => proSeriesCategory.id === category.id
    )

    if (thisCategory && thisCategory.proSeriesSubcategories.length > 0) {
      subcategories = thisCategory.proSeriesSubcategories.map(
        (subcategory) => ({
          url: getProSeriesSubcategoryUrl(category.slug, subcategory.slug),
          image: subcategory.icon?.responsiveImage,
          title:
            category.name == "Pro-Series Seats"
              ? "Pro-" + subcategory.name
              : subcategory.name,
        })
      )
    }

    header = category.name
    subheader = series
    body = category.bannerBody
    image = category.bannerImage?.responsiveImage
    breadcrumbs = [
      {
        link: getProSeriesUrl(
          module == "automotive" ? "pro-series" : series.toLowerCase()
        ),
        label: series,
      },
      {
        link: "#",
        label: category.name,
      },
    ]
  } else {
    // console.log(data)
    categories = data?.collectionsConfiguration?.proSeries
      ?.map((cat) => ({
        url: getProSeriesCategoryUrl(
          cat.proSeriesCategory.slug,
          module == "automotive" ? undefined : series.toLowerCase()
        ),
        image: cat.proSeriesCategory.categoryIcon?.responsiveImage,
        title: cat.proSeriesCategory.name,
        module: cat.module.name.toLowerCase(),
        series: cat.proSeriesCategory.powersportsSeries,
      }))
      .filter(
        (cat) =>
          (cat.module == module || cat.module == "all") &&
          (series == "Pro-Series" ||
            series.toLowerCase() == cat.series.toLowerCase())
      )
    header = series
  }

  const filterCount = activeFilters
    ? Object.keys(activeFilters).reduce((totalCount, key) => {
        return activeFilters[key].length > 0
          ? totalCount + activeFilters[key].length
          : totalCount
      }, 0)
    : 0

  const { onViewCollection } = useGtmEvents()

  React.useEffect(() => {
    if (data && dataLoaded) {
      onViewCollection({
        category: category?.name,
        subcategory: subcategory?.name,
      })
    }
  }, [data, dataLoaded])

  return (
    <Layout module={module}>
      <Helmet
        title={title || data?.page?.title || "TMI Products"}
        module={module}
      />
      <VerticalSpace height={20} mobileHeight={32} />
      {category ? (
        <Banner
          backgroundImage={
            image ??
            data?.collectionsConfiguration?.defaultBannerImage?.responsiveImage
          }
          header={header}
          subheader={subheader}
          body={body}
        />
      ) : (
        <Banner
          backgroundImage={
            module == "powersports"
              ? series.toLowerCase() == "interior"
                ? data?.collectionsConfiguration?.powersportsInteriorBanner
                    ?.responsiveImage ||
                  data?.collectionsConfiguration?.defaultBannerImage
                    ?.responsiveImage
                : data?.collectionsConfiguration?.powersportsExteriorBanner
                    ?.responsiveImage ||
                  data?.collectionsConfiguration?.defaultBannerImage
                    ?.responsiveImage
              : data?.collectionsConfiguration?.proSeriesBannerImage
                  ?.responsiveImage ||
                data?.collectionsConfiguration?.defaultBannerImage
                  ?.responsiveImage
          }
          header={header}
          subheader={subheader}
          body={body}
        />
      )}
      {categories && categories.length > 0 && (
        <>
          <Categories categories={categories} />
        </>
      )}
      {subcategories && subcategories.length > 0 && (
        <>
          <ProductStyles productStyles={subcategories} type={"Type"} />
        </>
      )}
      {styles && styles.length > 0 && (
        <>
          <ProductStyles productStyles={styles} />
        </>
      )}
      <VerticalSpace height={32} mobileHeight={24} />
      {breadcrumbs && (
        <>
          <MobileBreadcrumbs breadcrumbs={breadcrumbs} />
          <VerticalSpace height={0} mobileHeight={20} />
        </>
      )}
      {products[sortMethod] && products[sortMethod].length > 0 && (
        <SortDropdown value={sortMethod} onSort={onSort} />
      )}
      {breadcrumbs && <DesktopBreadcrumbs breadcrumbs={breadcrumbs} />}
      <VerticalSpace height={32} mobileHeight={0} />
      <FilterButton filterCount={filterCount} onClick={openMobileFilters} />
      <Products
        containerRef={productListRef}
        showFilters
        filters={filters}
        activeFilters={activeFilters}
        onFilter={onFilter}
        onClearFilters={onClearFilter}
        products={renderedProducts}
        showEachForSingleSoldAs
        defaultProductImage={
          data?.collectionsConfiguration?.defaultProductCardImage
            ?.responsiveImage
        }
      />
      {dataLoaded && allProducts?.length === 0 && <Empty />}
      <VerticalSpace height={84} mobileHeight={28} />
      <Filters
        isOpen={showMobileFilters}
        filters={filters}
        activeFilters={activeFilters}
        onFilter={onFilter}
        onClearFilters={onClearFilter}
        onRequestClose={closeMobileFilters}
      />
      {category?.slug == "complete-seats" ? <ConfigBanner /> : ""}
    </Layout>
  )
}

export default ProSeries
